import { motion } from "framer-motion";

export const ArticlePageSkeletonLoader = () => {
  const shimmer = {
    backgroundPosition: ["0% 0%", "100% 0%"],
  };

  const shimmerTransition = {
    duration: 1.5,
    repeat: Number.POSITIVE_INFINITY,
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-[#000456] via-[#00067E] via-[#0a1a8a] to-[#1e3a8a] overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] via-[#0a1a8a] via-[#1e3a8a] to-[#3b82f6] opacity-90" />
      </div>

      <main className="relative z-10 mt-16 md:mt-18 py-16 md:py-20">
        <div className="container max-w-7xl mx-auto">
          {/* Article Container */}
          <article className="font-jakarta max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
            
            {/* Hero Image Skeleton */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="w-full h-80 bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded-2xl mb-8"
              style={{
                backgroundSize: "200% 100%",
              }}
            //   animate={shimmer}
            //   transition={shimmerTransition}
            />

            {/* Article Meta (Category, Reading Time) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex gap-4 mb-6"
            >
              <div
                className="h-6 w-24 bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded-full"
                style={{
                  backgroundSize: "200% 100%",
                }}
                animate={shimmer}
                transition={shimmerTransition}
              />
              <div
                className="h-6 w-28 bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded-full"
                style={{
                  backgroundSize: "200% 100%",
                }}
                animate={shimmer}
                transition={shimmerTransition}
              />
            </motion.div>

            {/* Title Skeleton */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-3 mb-6"
            >
              <div
                className="h-10 w-full bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded"
                style={{
                  backgroundSize: "200% 100%",
                }}
                animate={shimmer}
                transition={shimmerTransition}
              />
              <div
                className="h-10 w-4/5 bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded"
                style={{
                  backgroundSize: "200% 100%",
                }}
                animate={shimmer}
                transition={shimmerTransition}
              />
            </motion.div>

            {/* Publish Date and Author */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mb-8"
            >
              <div
                className="h-5 w-64 bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded"
                style={{
                  backgroundSize: "200% 100%",
                }}
                animate={shimmer}
                transition={shimmerTransition}
              />
            </motion.div>

            {/* Excerpt/Meta Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mb-8 space-y-2"
            >
              <div
                className="h-5 w-full bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded"
                style={{
                  backgroundSize: "200% 100%",
                }}
                animate={shimmer}
                transition={shimmerTransition}
              />
              <div
                className="h-5 w-5/6 bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded"
                style={{
                  backgroundSize: "200% 100%",
                }}
                animate={shimmer}
                transition={shimmerTransition}
              />
            </motion.div>

            {/* Divider */}
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="h-px bg-gradient-to-r from-transparent via-[#4f46e5]/30 to-transparent my-8"
            />

            {/* Content Paragraphs */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + i * 0.1 }}
                className="mb-6 space-y-2"
              >
                {/* Paragraph with varied line lengths */}
                {[...Array(3 + Math.floor(Math.random() * 3))].map((_, lineIdx) => {
                  const widths = ["w-full", "w-full", "w-5/6"];
                  const width = widths[Math.floor(Math.random() * widths.length)];
                  return (
                    <div
                      key={lineIdx}
                      className={`h-5 ${width} bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded`}
                      style={{
                        backgroundSize: "200% 100%",
                      }}
                      animate={shimmer}
                      transition={shimmerTransition}
                    />
                  );
                })}
              </motion.div>
            ))}

            {/* Section Heading Skeleton */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="my-10 mb-6"
            >
              <div
                className="h-8 w-2/3 bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded"
                style={{
                  backgroundSize: "200% 100%",
                }}
                animate={shimmer}
                transition={shimmerTransition}
              />
            </motion.div>

            {/* More Content Paragraphs */}
            {[...Array(4)].map((_, i) => (
              <motion.div
                key={`section-${i}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.3 + i * 0.1 }}
                className="mb-6 space-y-2"
              >
                {[...Array(2 + Math.floor(Math.random() * 2))].map((_, lineIdx) => {
                  const widths = ["w-full", "w-full", "w-4/5"];
                  const width = widths[Math.floor(Math.random() * widths.length)];
                  return (
                    <div
                      key={lineIdx}
                      className={`h-5 ${width} bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded`}
                      style={{
                        backgroundSize: "200% 100%",
                      }}
                      animate={shimmer}
                      transition={shimmerTransition}
                    />
                  );
                })}
              </motion.div>
            ))}
          </article>

          {/* CTA Section Skeleton */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.8 }}
            className="text-center mt-16 mb-8"
          >
            {/* Heading */}
            <div className="space-y-3 mb-6 max-w-2xl mx-auto">
              <div
                className="h-8 w-full bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded"
                style={{
                  backgroundSize: "200% 100%",
                }}
                animate={shimmer}
                transition={shimmerTransition}
              />
              <div
                className="h-8 w-4/5 mx-auto bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded"
                style={{
                  backgroundSize: "200% 100%",
                }}
                animate={shimmer}
                transition={shimmerTransition}
              />
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <div
                className="h-12 w-48 bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded-xl mx-auto sm:mx-0"
                style={{
                  backgroundSize: "200% 100%",
                }}
                animate={shimmer}
                transition={shimmerTransition}
              />
              <div
                className="h-12 w-48 bg-gradient-to-r from-[#4f46e5]/10 via-[#6366f1]/20 to-[#4f46e5]/10 rounded-xl mx-auto sm:mx-0"
                style={{
                  backgroundSize: "200% 100%",
                }}
                animate={shimmer}
                transition={shimmerTransition}
              />
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};
